import React, { Fragment, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  GetTicketContextDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../utils/integrationsTypes";
import useGenerateIntegrationName from "../../../utils/integrationsUtils";
import { ZendeskFormStepOne } from "./components/ZendeskFormStepOne";
import { ZendeskFormStepTwo } from "./components/ZendeskFormStepTwo";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export type ZendeskFormInput = {
  integrationName: string;
  subdomain: string;
  email: string;
  apiToken: string;
  autoCloseTickets: boolean;
  autoCreateTickets: boolean;
  useCustomFields: boolean;
  customFields: Array<{
    id: string;
    value: string;
  }>;
};

const defaultValues: ZendeskFormInput = {
  integrationName: "",
  subdomain: "",
  email: "",
  apiToken: "",
  autoCloseTickets: false,
  autoCreateTickets: false,
  useCustomFields: false,
  customFields: [
    {
      id: "",
      value: "",
    },
  ],
};

interface ZendeskIntegrationFormProps {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}

export function ZendeskIntegrationForm({
  scope,
  updateFlow,
}: ZendeskIntegrationFormProps) {
  const [searchParams] = useSearchParams();
  const redirectToParam = searchParams.get("redirectTo");
  const stepFromParams = searchParams.get("step");
  const [step, setStep] = useState<number>(Number(stepFromParams) || 1);
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
    watch,
  } = useForm<ZendeskFormInput>({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [watchAutoCreateOnDrift, useCustomFields] = watch([
    "autoCreateTickets",
    "useCustomFields",
  ]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      GetTicketContextDocument,
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      const configOptions = updateFlow.integration.configurationOptions;
      if (configOptions?.__typename !== "ZendeskConfigurationOptions") return;

      reset({
        ...defaultValues,
        subdomain: configOptions.subdomain,
        email: configOptions.email,
        integrationName: updateFlow.integration.name,
        autoCloseTickets: configOptions.autoCloseTickets,
        autoCreateTickets: configOptions.autoCreateTickets,
        customFields:
          (configOptions.customFields?.length || 0) > 0
            ? configOptions.customFields
                ?.flatMap((x) => x ?? [])
                .map((x) => ({
                  id: String(x.id),
                  value: x.value,
                }))
            : defaultValues.customFields,
        useCustomFields: (configOptions.customFields?.length || 0) > 0,
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<ZendeskFormInput> = async (data) => {
    const zendeskConfigurationOptions = {
      subdomain: data.subdomain.trim(),
      email: data.email.trim(),
      apiToken: data.apiToken?.trim?.() || "",
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
      customFields: data.useCustomFields
        ? data.customFields
            .filter((x) => x.id && x.value)
            .map((x) => ({
              id: Number(x.id),
              value: x.value,
            }))
        : [],
    };

    const integrationName =
      data.integrationName?.trim() || updateFlow?.integration.name;

    try {
      if (updateFlow || step === 2) {
        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.TicketSystemZendesk,
              configurationOptions: {
                zendeskConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow?.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        if (step === 1 && updateFlow) {
          setStep(2);
          return;
        } else {
          navigate(
            `/space/integrations/ticket_system_zendesk/${integrationId}/?${scope.params}`,
          );
        }
      } else {
        const { data: createIntegrationData } = await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.TicketSystemZendesk,
              longLivedToken: false,
              configurationOptions: {
                zendeskConfigurationOptions,
              },
            },
          },
        });
        const integrationId =
          createIntegrationData?.createClientIntegration.integration.mrn
            .split("/")
            .pop();
        if (step === 1) {
          navigate(
            `/space/integrations/ticket_system_zendesk/${integrationId}/edit?${scope.params}&step=2`,
          );
          return;
        }
      }
      if (redirectToParam) {
        navigate(redirectToParam);
      } else {
        navigate(`/space/integrations/ticket_system_zendesk?${scope.params}`);
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["Zendesk", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader
        {...{ type: IntegrationType.TicketSystemZendesk }}
      />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {step === 1 && (
            <ZendeskFormStepOne
              control={control}
              isUpdateFlow={!!updateFlow}
              errors={errors}
              getFieldState={getFieldState}
              isValid={isValid}
              disabled={!watchAutoCreateOnDrift}
            />
          )}

          {step === 2 && (
            <ZendeskFormStepTwo
              control={control}
              isValid={isValid}
              isUpdateFlow={!!updateFlow}
              errors={errors}
              showCustomFields={useCustomFields}
            />
          )}
        </form>
      </Box>
    </Fragment>
  );
}
